import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button, Grid, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';
import { useDebouncedCallback } from 'use-debounce';

import { ReactComponent as EmptyDarkImage } from 'assets/images/empty-dark.svg';
import { ReactComponent as EmptyLightImage } from 'assets/images/empty-light.svg';
import ConditionalInputMessage from 'components/ConditionalInputMessage';
import EmptyState from 'components/EmptyState';
import LoadingOverlay from 'components/LoadingOverlay';
import ModalEditarMensagem from 'components/ModalEditarMensagem';
import ModalHistoricoMensagemEditada from 'components/ModalHistoricoMensagemEditada';
import ModalInatividadeAtendente from 'components/ModalInatividadeAtendente';
import NewMessagesContainer from 'components/NewMessagesContainer';
import PasteOrDragAndDropFiles from 'components/PasteOrDragAndDropFiles';
import PesquisarMensagensContainer from 'components/PesquisarMensagensContainer';
import SnackMessage from 'components/SnackMessage';
import * as emitterEvents from 'constants/emitterEvents';
import {
  LOCAL_STORAGE_KEY_INPUT,
  LOCAL_STORAGE_KEY_INPUT_MENTIONS,
} from 'constants/localStorageKeys';
import * as socketEvents from 'constants/socketEvents';
import useCurrentUser from 'hooks/useCurrentUser';
import { FileWithCaption } from 'hooks/useFileManager';
import { useNavigateToMessage } from 'hooks/useNavigateToMessage';
import { Sounds, useSound } from 'hooks/useSound';
import Emitter from 'services/eventEmitter';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoInterface,
  atendimentosSelector,
  checkInactivity,
  MessageSocketError,
  setAtendimentoSelecionado,
  setshowModalAlertaInatividade,
  fetchMarcarAtendimentoComoLido,
  setTotalMensagensNaoLidasAtendimentoIniciado,
  StatusSessaoEnum,
  updateListaDeIniciadosAlterarSessao,
  updateSessaoExpiraEm,
  updateListaDeIniciadosBloqueioContato,
} from 'store/reducers/atendimentos';
import { TipoCanalEnum } from 'store/reducers/canais';
import {
  ContatoInterface,
  fetchContatoAtendimento,
} from 'store/reducers/contatos';
import {
  addMensagemAtendimento,
  atualizarMensagemAtendimento,
  atualizarMensagemEditadaAtendimento,
  AtualizarMensagemEditadaAtendimentoInterface,
  AtualizarMidiaMensagemInterface,
  atualizarMidiaMensagensAtendimento,
  atualizarReacoesMensagemAtendimento,
  AtualizarReacoesMensagemInterface,
  atualizarStatusMensagensAtendimento,
  atualizarTranscricaoAudio,
  BaixandoMidiaMensagemInterface,
  baixandoMidiaMensagensAtendimento,
  enviarArquivo,
  fetchMensagensByAtendimento,
  MensagemCtaInterface,
  MensagemInterface,
  mensagensSelector,
  resetAtendimentoCursorAConsumir,
  resetIdMensagemPesquisada,
  resetMensagensAtendimento,
  resetMensagensParaEncaminhar,
  resetPreviewMensagens,
  resetReplyMessage,
  setAfterCursorAConsumir,
  setBeforeCursorAConsumir,
  setCursorAConsumir,
  setLoadingEnviarArquivo,
  setMensagemApagadaAtendimento,
  setReplyMessage,
  StatusMensagemType,
  TiposMensagemEnum,
} from 'store/reducers/mensagens';
import { TemplateInterface } from 'store/reducers/templates';
import has15MinutesPassed from 'utils/has15MinutesPassed';

import AlertaIndisponibilidade from './components/AlertaIndisponibilidade';
import ListaAtendimentosIniciadosVirtualizada from './components/ListaAtendimentosIniciadoVirtualizada';
import TopContentAppBar from './components/TopContentAppBar';

const useStyles = makeStyles(({ palette, shape, spacing, zIndex }) => ({
  root: {
    position: 'relative',
    display: 'flex',
    height: '100%',
    gap: '16px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  barraMensagensNaoLidas: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    zIndex: zIndex.appBar + 1,
    color: palette.getContrastText(palette.primary.main),
    backgroundColor: palette.primary.main,
    borderBottomLeftRadius: shape.borderRadius,
    borderBottomRightRadius: shape.borderRadius,
    padding: spacing(1),
    position: 'absolute',
    top: spacing(7),
    left: 0,
    right: 0,
  },
}));

const SEND_TEXT_TIMEOUT_MS = 15000;

export default function Atendimentos() {
  const classes = useStyles();
  const theme = useTheme();
  const { play } = useSound();
  const currentUser = useCurrentUser();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { handleNavigateToMessage } = useNavigateToMessage();

  const [initialLoadingMesssages, setInitialLoadingMesssages] = useState(false);
  const [mensagemASerEditada, setMensagemASerEditada] = useState<
    MensagemInterface | undefined
  >();
  const [filesSelected, setFilesSelected] = useState<File[]>([]);

  const [mensagemId, setMensagemId] = useState<string | undefined>();
  const [pesquisarMensagens, setPesquisarMensagens] = useState(false);

  const dispatch = useAppDispatch();
  const {
    totalFila,
    atendimentoSelecionado,
    atendimentosIniciados,
    loadingFinalizarAtendimento,
    loadingAtendimentosIniciados,
  } = useAppSelector(atendimentosSelector);

  const {
    isGravandoAudio,
    mensagemIdPesquisada,
    atendimentoCursorAConsumir,
    mensagensAtendimento,
    mensagensAtendimentoCursor,
    loadingMensagensAtendimento,
    replyMessage,
    searchQuery,
  } = useAppSelector(mensagensSelector);

  const totalIniciados = useMemo(() => {
    return atendimentosIniciados.length ?? 0;
  }, [atendimentosIniciados.length]);

  const totalMsgsNaoLidas = useMemo(() => {
    return atendimentoSelecionado?.totalMensagensNaoLidas ?? 0;
  }, [atendimentoSelecionado]);

  const handleSelectFiles = (files: File[]) => {
    setFilesSelected(files);
  };

  /**
   * Emite um evento para o componente de mensagens
   * avisando que uma nova mensagem foi enviada pelo [atendente]
   */
  const sendMessageAtendenteNoScrollTop = (value: boolean) => {
    Emitter.emit(
      emitterEvents.ENVIAR_NOVA_MENSAGEM_ATENDENTE_NO_SCROLL_TOP,
      value,
    );
  };

  /** Send TextMessage */
  const handleSendText = (
    texto: string,
    callback: (isSuccess: boolean) => void,
  ) => {
    const atendimentoId = atendimentoSelecionado?.id;
    const mensagemCitadaId = replyMessage?.id;

    const timeoutRef = setTimeout(() => {
      callback(false);
    }, SEND_TEXT_TIMEOUT_MS);

    if (atendimentoId) {
      const data = { atendimentoId, mensagem: texto, mensagemCitadaId };
      socket.emit(
        socketEvents.EMIT_ENVIAR_MENSAGEM,
        data,
        (msgEnviada: MensagemInterface | MessageSocketError) => {
          if ('error' in msgEnviada) {
            enqueueSnackbar(msgEnviada.error);
            callback(false);
            clearTimeout(timeoutRef);
            return;
          }

          callback(true);
          onResetReplyMessage();
          clearTimeout(timeoutRef);
          dispatch(addMensagemAtendimento(msgEnviada));
          sendMessageAtendenteNoScrollTop(true);
        },
      );
    }
  };

  const handleSendInstagramTagMessage = (
    texto: string,
    callback: (isSuccess: boolean) => void,
  ) => {
    const atendimentoId = atendimentoSelecionado?.id;
    const mensagemCitadaId = replyMessage?.id;

    const timeoutRef = setTimeout(() => {
      callback(false);
    }, SEND_TEXT_TIMEOUT_MS);

    if (atendimentoId) {
      const data = {
        atendimentoId,
        mensagem: texto,
        subtype: 'TAGGED',
        mensagemCitadaId,
      };
      socket.emit(
        socketEvents.EMIT_ENVIAR_MENSAGEM,
        data,
        (msgEnviada: MensagemInterface | MessageSocketError) => {
          if ('error' in msgEnviada) {
            enqueueSnackbar(msgEnviada.error);
            callback(false);
            clearTimeout(timeoutRef);
            return;
          }

          callback(true);
          onResetReplyMessage();
          clearTimeout(timeoutRef);
          dispatch(addMensagemAtendimento(msgEnviada));
          sendMessageAtendenteNoScrollTop(true);
        },
      );
    }
  };

  const handleSendCTA = (
    whatsappInteractiveComponents?: MensagemCtaInterface,
  ) => {
    const atendimentoId = atendimentoSelecionado?.id;

    if (atendimentoId) {
      const data = {
        atendimentoId,
        whatsappInteractiveComponents,
        tipo: TiposMensagemEnum.WPP_CTA,
      };

      socket.emit(
        socketEvents.EMIT_ENVIAR_MENSAGEM,
        data,
        (msgEnviada: MensagemInterface | MessageSocketError) => {
          if ('error' in msgEnviada) {
            enqueueSnackbar(msgEnviada.error);
            return;
          }

          dispatch(addMensagemAtendimento(msgEnviada));
          sendMessageAtendenteNoScrollTop(true);
        },
      );
    }
  };

  const handleSendTemplate = (templateSelecionado: TemplateInterface) => {
    const whatsappTemplateId = templateSelecionado.id;
    const atendimentoId = atendimentoSelecionado?.id;

    if (whatsappTemplateId && atendimentoId) {
      const whatsappTemplate = {
        id: whatsappTemplateId,
        name: templateSelecionado.wppName,
        language: templateSelecionado.wppLanguage,
        whatsAppTemplateFormFields: templateSelecionado.formFields,
      };

      const data = {
        atendimentoId,
        whatsappTemplate,
        tipo: TiposMensagemEnum.TEMPLATE_WHATSAPP,
      };

      socket.emit(
        socketEvents.EMIT_ENVIAR_MENSAGEM,
        data,
        (msgEnviada: MensagemInterface | MessageSocketError) => {
          if ('error' in msgEnviada) {
            enqueueSnackbar(msgEnviada.error, { variant: 'error' });
            return;
          }

          dispatch(addMensagemAtendimento(msgEnviada));
        },
      );
    }
  };

  /** Send ContatoMessage */
  const handleSendContato = (contato: ContatoInterface) => {
    const atendimentoId = atendimentoSelecionado?.id;
    const mensagemCitadaId = replyMessage?.id;

    if (atendimentoId) {
      const data = {
        atendimentoId,
        tipo: 'CONTATO',
        mensagemContato: contato,
        mensagemCitadaId,
      };

      socket.emit(
        socketEvents.EMIT_ENVIAR_MENSAGEM,
        data,
        (msgEnviada: MensagemInterface | MessageSocketError) => {
          if ('error' in msgEnviada) {
            enqueueSnackbar(msgEnviada.error);
            return;
          }

          onResetReplyMessage();
          dispatch(addMensagemAtendimento(msgEnviada));
          sendMessageAtendenteNoScrollTop(true);
        },
      );
    }
  };

  /** Send AudioMessage */
  const handleSendAudio = (arquivo: Blob, duracao: number) => {
    const atendimentoId = atendimentoSelecionado?.id;
    const mensagemCitadaId = replyMessage?.id;

    if (atendimentoId) {
      const data = {
        arquivo,
        atendimentoId,
        duracao,
        isGravacaoAudio: true,
        mensagemCitadaId,
      };

      dispatch(enviarArquivo(data))
        .unwrap()
        .then(msgEnviada => {
          onResetReplyMessage();
          dispatch(addMensagemAtendimento(msgEnviada));
          sendMessageAtendenteNoScrollTop(true);
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
        });
    }
  };

  const handleSendFiles = (
    files: FileWithCaption[],
    callback?: (isSuccess: boolean) => void,
  ) => {
    if (!atendimentoSelecionado) {
      if (callback) callback(false);
      return;
    }

    const atendimentoId = atendimentoSelecionado.id;
    const mensagemCitadaId = replyMessage?.id;

    dispatch(setLoadingEnviarArquivo(true));

    const promises = files.map(fileOption => {
      const data = {
        arquivo: fileOption.file,
        atendimentoId,
        mensagemCitadaId,
        midiaTitulo: fileOption.caption,
      };

      return dispatch(enviarArquivo(data))
        .unwrap()
        .then(msgEnviada => {
          if (!atendimentoCursorAConsumir[0]?.beforeCursor) {
            dispatch(addMensagemAtendimento(msgEnviada));
          }

          sendMessageAtendenteNoScrollTop(true);
          return { success: true };
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
          dispatch(setLoadingEnviarArquivo(false));
          return { success: false };
        });
    });

    Promise.allSettled(promises).then(results => {
      const allSuccess = results.every(
        r =>
          r.status === 'fulfilled' &&
          (r as PromiseFulfilledResult<{ success: boolean }>).value?.success,
      );

      onResetReplyMessage();
      if (callback) callback(allSuccess);
      if (allSuccess) {
        setFilesSelected([]);
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT);
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS);
        dispatch(setLoadingEnviarArquivo(false));
      }
    });
  };

  /** Send from ModalGaleria */
  const handleSendFilesFromGaleria = (storageIds: string[]) => {
    const atendimentoId = atendimentoSelecionado?.id;

    if (atendimentoId) {
      storageIds.forEach(storageId => {
        const data = { atendimentoId, storageId };

        socket.emit(
          socketEvents.EMIT_ENVIAR_ARQUIVO_GALERIA,
          data,
          (msgEnviada: MensagemInterface | MessageSocketError) => {
            if ('error' in msgEnviada) {
              enqueueSnackbar(msgEnviada.error);
              return;
            }

            dispatch(addMensagemAtendimento(msgEnviada));
            sendMessageAtendenteNoScrollTop(true);
          },
        );
      });
    }
  };

  const onLoadNextMessages = useCallback(() => {
    const { afterCursor: afterAConsumir } = atendimentoCursorAConsumir[0] || {};
    const { afterCursor } = mensagensAtendimentoCursor;

    if (atendimentoSelecionado) {
      const atendimentoId = atendimentoSelecionado.id;

      if (mensagemIdPesquisada) {
        if (afterAConsumir) {
          dispatch(
            fetchMensagensByAtendimento({
              atendimentoId,
              cursor: { afterCursor: afterAConsumir },
            }),
          )
            .unwrap()
            .then(data => {
              const { afterCursor: currentAfterCursor } = data.cursor;

              dispatch(
                setAfterCursorAConsumir({ afterCursor: currentAfterCursor }),
              );
            });
        }
      } else {
        dispatch(
          fetchMensagensByAtendimento({
            atendimentoId,
            cursor: { afterCursor },
          }),
        )
          .unwrap()
          .then(data => {
            const { afterCursor } = data.cursor;

            dispatch(setAfterCursorAConsumir({ afterCursor }));
          });
      }
    }
  }, [
    atendimentoSelecionado,
    atendimentoCursorAConsumir,
    dispatch,
    mensagemIdPesquisada,
    mensagensAtendimentoCursor,
  ]);

  const onLoadPreviousMessages = useCallback(() => {
    const { beforeCursor: beforeAConsumir } =
      atendimentoCursorAConsumir[0] || {};
    const { beforeCursor } = mensagensAtendimentoCursor;

    if (atendimentoSelecionado) {
      const atendimentoId = atendimentoSelecionado.id;

      if (mensagemIdPesquisada) {
        if (beforeAConsumir) {
          dispatch(
            fetchMensagensByAtendimento({
              atendimentoId,
              cursor: { beforeCursor: beforeAConsumir },
            }),
          )
            .unwrap()
            .then(data => {
              const { beforeCursor: currentBeforeCursor } = data.cursor;

              dispatch(
                setBeforeCursorAConsumir({ beforeCursor: currentBeforeCursor }),
              );
            });
        }
      } else {
        dispatch(
          fetchMensagensByAtendimento({
            atendimentoId,
            cursor: { beforeCursor },
          }),
        )
          .unwrap()
          .then(data => {
            const { beforeCursor } = data.cursor;

            dispatch(setBeforeCursorAConsumir({ beforeCursor }));
          });
      }
    }
  }, [
    atendimentoSelecionado,
    atendimentoCursorAConsumir,
    dispatch,
    mensagemIdPesquisada,
    mensagensAtendimentoCursor,
  ]);

  const handleResetLista = useCallback(() => {
    const atendimentoId = atendimentoSelecionado?.id;

    if (atendimentoId) {
      setInitialLoadingMesssages(true);
      dispatch(resetIdMensagemPesquisada());
      dispatch(resetAtendimentoCursorAConsumir());
      dispatch(resetMensagensAtendimento());
      dispatch(fetchMensagensByAtendimento({ atendimentoId }))
        .unwrap()
        .then(data => {
          const { cursor } = data;
          dispatch(setCursorAConsumir(cursor));
        })
        .finally(() => {
          setInitialLoadingMesssages(false);
        });
    }
  }, [atendimentoSelecionado, dispatch]);

  const handleConfirmarLeituraMensagens = (force = false) => {
    const atendimentoId = atendimentoSelecionado?.id;

    if (
      atendimentoId &&
      (force || totalMsgsNaoLidas > 0 || totalMsgsNaoLidas === -1)
    ) {
      dispatch(fetchMarcarAtendimentoComoLido(atendimentoId));

      dispatch(
        setTotalMensagensNaoLidasAtendimentoIniciado({
          atendimentoId,
          total: 0,
        }),
      );
    }
  };

  const handleReenviarMensagem = (mensagem: MensagemInterface) => {
    const { atendimentoId, id: mensagemId } = mensagem;
    if (atendimentoId) {
      const data = { id: mensagemId, atendimentoId };
      socket.emit(socketEvents.EMIT_REENVIAR_MENSAGEM, data);
    }
  };

  const handleApagarMensagem = (mensagemId: string) => {
    const rules = atendimentoSelecionado?.rules;

    if (rules?.noRemoveMessage) {
      enqueueSnackbar(rules.noRemoveMessage);
    } else {
      socket.emit(socketEvents.EMIT_APAGAR_MENSAGEM, { mensagemId });
    }
  };

  const handleEditarMensagem = (
    mensagem: MensagemInterface,
    newText: string,
  ) => {
    const rules = atendimentoSelecionado?.rules;

    if (rules?.noEditMessage) {
      enqueueSnackbar(rules.noEditMessage);
    } else {
      const data = {
        atendimentoId: mensagem.atendimentoId,
        mensagem: newText,
        mensagemId: mensagem.id,
      };

      socket.emit(
        socketEvents.EMIT_EDITAR_MENSAGEM,
        data,
        (mensagem: MensagemInterface) => {
          dispatch(atualizarMensagemAtendimento(mensagem));
          hideModalEditarMensagem();
          setMensagemASerEditada(undefined);
        },
      );
    }
  };

  const handleResponderMensagem = (mensagem: MensagemInterface) => {
    const rules = atendimentoSelecionado?.rules;

    if (rules?.noReplyMessage) {
      enqueueSnackbar(rules.noReplyMessage);
    } else {
      dispatch(setReplyMessage(mensagem));
    }
  };

  const onResetReplyMessage = useCallback(() => {
    dispatch(resetReplyMessage());
  }, [dispatch]);

  const [showModalEditarMensagem, hideModalEditarMensagem] = useModal(
    ({ in: open }) =>
      mensagemASerEditada ? (
        <ModalEditarMensagem
          open={open}
          onClose={hideModalEditarMensagem}
          mensagem={mensagemASerEditada}
          handleEditarMensagem={handleEditarMensagem}
        />
      ) : null,
    [mensagemASerEditada?.mensagem],
  );

  const onEditarMensagem = (mensagem: MensagemInterface) => {
    if (has15MinutesPassed(mensagem.criadoEm, new Date())) {
      enqueueSnackbar(
        'As mensagens não podem ser editadas após 15 minutos. ⏳',
      );
      return;
    }

    setMensagemASerEditada(mensagem);
    showModalEditarMensagem();
  };

  const [showModalHistoricoMensagemEditada, hideModalHistoricoMensagemEditada] =
    useModal(
      ({ in: open }) =>
        mensagemId ? (
          <ModalHistoricoMensagemEditada
            open={open}
            onClose={hideModalHistoricoMensagemEditada}
            mensagemId={mensagemId}
          />
        ) : null,
      [mensagemId],
    );

  const handleVerHistoricoMensagemEditada = (mensagemId: string) => {
    setMensagemId(mensagemId);
    showModalHistoricoMensagemEditada();
  };

  const [showAlertaInatividadeAtendente, hideAlertaInatividadeAtendente] =
    useModal(
      ({ in: open }) => {
        if (!atendimentoSelecionado) return null;

        return (
          <ModalInatividadeAtendente
            atendimentoId={atendimentoSelecionado.id}
            open={open}
            onClose={hideAlertaInatividadeAtendente}
          />
        );
      },
      [atendimentoSelecionado?.id],
    );

  const handleAlertaInatividade = useCallback(
    (data: { atendimentoId: string }) => {
      if (atendimentoSelecionado?.id === data.atendimentoId) {
        dispatch(setshowModalAlertaInatividade(true));
        showAlertaInatividadeAtendente();
      }
    },
    [atendimentoSelecionado?.id, showAlertaInatividadeAtendente, dispatch],
  );

  // event listeners
  useEffect(() => {
    const atendimentoId = atendimentoSelecionado?.id;

    if (atendimentoId) {
      // inicia o loading sempre que começa o carregamento das mensagens do atendimento selecionado
      setInitialLoadingMesssages(true);
      setPesquisarMensagens(false);
      dispatch(resetPreviewMensagens());
      dispatch(resetIdMensagemPesquisada());
      // Recarrega as mensagens do atendimento selecionado
      dispatch(resetMensagensAtendimento());
      dispatch(fetchMensagensByAtendimento({ atendimentoId }))
        .unwrap()
        .then(data => {
          const { cursor } = data;
          dispatch(setCursorAConsumir(cursor));
          dispatch(resetMensagensParaEncaminhar());
        })
        .finally(() => {
          setInitialLoadingMesssages(false);
          setMensagemASerEditada(undefined);
          onResetReplyMessage();
          dispatch(checkInactivity(atendimentoSelecionado.id))
            .unwrap()
            .then(({ alertInactivityAttendant }) => {
              if (alertInactivityAttendant) {
                showAlertaInatividadeAtendente();
              }
            });
        });

      // Observa os novos status de recebimento de mensagens no atendimento
      const handleMensagemStatus = (data: {
        ackStatus: StatusMensagemType;
        ackMotivoErro: string;
        ackDescricaoErro: string;
        ids: string[];
      }) => {
        dispatch(atualizarStatusMensagensAtendimento(data));
      };
      // Observa a reconexão do socket e recarrega as mensagens do atendimento selecionado
      const handleReconnect = () => {
        if (atendimentoId) {
          dispatch(resetMensagensAtendimento());
          dispatch(fetchMensagensByAtendimento({ atendimentoId }));
        }
      };
      // Observa se uma mensaggem do atendimento foi apagada
      const handleMensagemApagada = (data: {
        mensagemId: string;
        apagadaEm: string;
      }) => {
        dispatch(setMensagemApagadaAtendimento(data));
      };

      // Observa quando uma transcrição de áudio chegar via WebSocket
      const handleTranscricaoAudio = (data: {
        atendimentoId?: string;
        grupoChatId?: string;
        mensagem: { id: string; text: string };
        status: 'completed' | 'failed' | 'requested';
      }) => {
        if (data.atendimentoId === atendimentoId) {
          dispatch(
            atualizarTranscricaoAudio({
              atendimentoId: data.atendimentoId,
              mensagemId: data.mensagem.id,
              status: data.status,
              text: data.mensagem.text,
            }),
          );
        }
      };

      const handleSessaoAtualizada = (data: {
        atendimentoId: string;
        expireSessionIn: string;
      }) => {
        dispatch(
          updateSessaoExpiraEm({
            atendimentoId: data.atendimentoId,
            sessaoExpiraEm: data.expireSessionIn,
          }),
        );
      };

      socket.on(socketEvents.ON_MENSAGEM_STATUS, handleMensagemStatus);
      socket.on(socketEvents.ON_MENSAGEM_APAGADA, handleMensagemApagada);
      socket.on(socketEvents.ON_RECONNECT, handleReconnect);
      socket.on(socketEvents.ON_INATIVIDADE_ATENDENTE, handleAlertaInatividade);
      socket.on(socketEvents.ON_TRANSCRICAO_AUDIO, handleTranscricaoAudio);
      socket.on(socketEvents.ON_SESSAO_ATUALIZADA, handleSessaoAtualizada);

      return () => {
        socket.off(socketEvents.ON_MENSAGEM_STATUS, handleMensagemStatus);
        socket.off(socketEvents.ON_MENSAGEM_APAGADA, handleMensagemApagada);
        socket.off(socketEvents.ON_RECONNECT, handleReconnect);
        socket.off(
          socketEvents.ON_INATIVIDADE_ATENDENTE,
          handleAlertaInatividade,
        );
        socket.off(socketEvents.ON_TRANSCRICAO_AUDIO, handleTranscricaoAudio);
        socket.off(socketEvents.ON_SESSAO_ATUALIZADA, handleSessaoAtualizada);
      };
    }

    return () => null;
  }, [
    atendimentoSelecionado?.id,
    dispatch,
    handleAlertaInatividade,
    showAlertaInatividadeAtendente,
    onResetReplyMessage,
  ]);

  // Observa eventos de bloqueio/desbloqueio de contato enquanto houver atendimentos na lista de iniciados
  useEffect(() => {
    if (atendimentosIniciados.length === 0) return undefined;

    const handleContatoBloqueado = (data: {
      contatoId: string;
      bloqueadoEm?: string | null;
    }) => {
      dispatch(
        updateListaDeIniciadosBloqueioContato({
          contatoId: data.contatoId,
          bloqueadoEm: data.bloqueadoEm ?? new Date().toISOString(),
        }),
      );
    };

    const handleContatoDesbloqueado = (data: {
      contatoId: string;
      bloqueadoEm?: string | null;
    }) => {
      dispatch(
        updateListaDeIniciadosBloqueioContato({
          contatoId: data.contatoId,
          bloqueadoEm: data.bloqueadoEm ?? null,
        }),
      );
    };

    socket.on(socketEvents.ON_CONTATO_BLOQUEADO, handleContatoBloqueado);
    socket.on(socketEvents.ON_CONTATO_DESBLOQUEADO, handleContatoDesbloqueado);

    return () => {
      socket.off(socketEvents.ON_CONTATO_BLOQUEADO, handleContatoBloqueado);
      socket.off(
        socketEvents.ON_CONTATO_DESBLOQUEADO,
        handleContatoDesbloqueado,
      );
    };
  }, [atendimentosIniciados.length, dispatch]);

  const emitirSomNovaMensagemComDelay = useDebouncedCallback(() => {
    play(Sounds.NOVA_MENSAGEM);
  }, 3000); // VERSÃO NOVA

  // Observa o recebimento de novas mensagens do contato na lista de atendimentos iniciados
  useEffect(
    () => {
      const handleReceberMensagem = (msgRecebida: MensagemInterface) => {
        const indexFila = atendimentosIniciados.findIndex(
          a => a.id === msgRecebida.atendimentoId,
        ); // VERSÃO NOVA

        // VERSÃO NOVA: Se a mensagem recebida for de um atendimento que não está na fila de iniciados, não reproduz o som
        if (indexFila === -1) return;

        // Emite um alerta sonoro independente se o atendimento está selecionado ou não,
        // porém se a mensagem for do tipo EVENTO, não reproduz o som
        // if (msgRecebida.tipo !== TiposMensagemEnum.EVENTO) {
        //   sound.play(Sounds.NOVA_MENSAGEM);
        // }

        // VERSÃO NOVA
        if (
          msgRecebida.tipo !== TiposMensagemEnum.EVENTO &&
          msgRecebida.autor !== 'ATENDENTE' &&
          msgRecebida.autor !== 'ATENDENTE_EXTERNO'
        ) {
          if (msgRecebida.atendimentoId === atendimentoSelecionado?.id) {
            play(Sounds.NOVA_MENSAGEM);
          } else {
            emitirSomNovaMensagemComDelay();
          }
        }

        // Add nova mensagem se a mesma pertencer ao atendimento selecionado
        if (
          msgRecebida.atendimentoId === atendimentoSelecionado?.id &&
          !atendimentoCursorAConsumir[0]?.beforeCursor
        ) {
          dispatch(addMensagemAtendimento(msgRecebida));
        }
      };

      socket.on(socketEvents.ON_RECEBER_MENSAGEM, handleReceberMensagem);

      return () => {
        socket.off(socketEvents.ON_RECEBER_MENSAGEM, handleReceberMensagem);
      };
    },
    [
      atendimentoSelecionado?.id,
      dispatch,
      atendimentosIniciados,
      emitirSomNovaMensagemComDelay,
      atendimentoCursorAConsumir,
      play,
      isGravandoAudio,
    ],
    // [atendimentoSelecionado?.id, dispatch]
  );

  // Observa evento de reação de mensagens
  useEffect(() => {
    const onReacaoMensagem = (data: AtualizarReacoesMensagemInterface) => {
      const {
        action,
        atendimentoId,
        author,
        mensagem: { contatoId },
        reactions: { msgReagidaId },
      } = data;

      if (contatoId) {
        dispatch(fetchContatoAtendimento(contatoId))
          .unwrap()
          .then(contato => {
            if (contato && action === 'add' && author === 'contato') {
              enqueueSnackbar('', {
                content: key => (
                  <SnackMessage
                    id={key}
                    message={
                      <Box>
                        <strong>{contato.nome}</strong>{' '}
                        <Typography style={{ fontSize: 12 }}>
                          Reagiu a uma mensagem.
                        </Typography>
                      </Box>
                    }
                    action={
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          closeSnackbar(key);
                          handleNavigateToMessage(msgReagidaId, atendimentoId);
                        }}
                      >
                        Visualizar
                      </Button>
                    }
                  />
                ),
              });
            }
          });
      }
      dispatch(atualizarReacoesMensagemAtendimento(data));
    };

    socket.on(socketEvents.ON_REACAO_MENSAGEM, onReacaoMensagem);

    return () => {
      socket.off(socketEvents.ON_REACAO_MENSAGEM, onReacaoMensagem);
    };
  });

  // evento de atualização de mídia
  useEffect(() => {
    const contatoId = atendimentoSelecionado?.contato.id;

    if (contatoId) {
      const onBaixandoMidiaMensagem = (
        data: BaixandoMidiaMensagemInterface,
      ) => {
        dispatch(baixandoMidiaMensagensAtendimento(data));
      };

      const onAtualizarMidiaMensagem = (
        data: AtualizarMidiaMensagemInterface,
      ) => {
        dispatch(atualizarMidiaMensagensAtendimento(data));
      };

      const onEdicaoMensagem = (
        data: AtualizarMensagemEditadaAtendimentoInterface,
      ) => {
        dispatch(atualizarMensagemEditadaAtendimento(data));
      };

      socket.on(
        socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM,
        onBaixandoMidiaMensagem,
      );
      socket.on(
        socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
        onAtualizarMidiaMensagem,
      );

      // socket.on(socketEvents.ON_REACAO_MENSAGEM, onReacaoMensagem);
      socket.on(socketEvents.ON_EDICAO_MENSAGEM, onEdicaoMensagem);

      return () => {
        socket.off(
          socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM,
          onBaixandoMidiaMensagem,
        );
        socket.off(
          socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
          onAtualizarMidiaMensagem,
        );

        // socket.off(socketEvents.ON_REACAO_MENSAGEM, onReacaoMensagem);
        socket.off(socketEvents.ON_EDICAO_MENSAGEM, onEdicaoMensagem);
      };
    }

    return () => null;
  }, [
    atendimentoSelecionado?.contato.id,
    dispatch,
    enqueueSnackbar,
    handleNavigateToMessage,
  ]);

  // Observa quando uma sessão for iniciada
  useEffect(() => {
    const handleIniciarSessaoAtendimento = ({
      atendimento,
    }: {
      atendimento: AtendimentoInterface;
    }) => {
      const sessaoStatus =
        atendimento.canal?.modoEnvio === 'API_NAO_OFICIAL'
          ? null
          : StatusSessaoEnum.OPEN;

      dispatch(
        updateListaDeIniciadosAlterarSessao({
          atendimentoId: atendimento.id,
          sessaoExpiraEm: atendimento.sessaoExpiraEm,
          sessaoContatoId: atendimento.sessaoContatoId,
          sessaoStatus,
        }),
      );

      if (atendimento.id === atendimentoSelecionado?.id) {
        dispatch(
          setAtendimentoSelecionado({
            ...atendimento,
            sessaoExpiraEm: atendimento.sessaoExpiraEm,
            sessaoContatoId: atendimento.sessaoContatoId,
            sessaoStatus,
          }),
        );
      }
    };

    socket.on(socketEvents.ON_SESSAO_INICIADA, handleIniciarSessaoAtendimento);

    return () => {
      socket.off(
        socketEvents.ON_SESSAO_INICIADA,
        handleIniciarSessaoAtendimento,
      );
    };
  }, [atendimentoSelecionado?.id, dispatch]);

  const handleResetInitialFiles = () => {
    setFilesSelected([]);
  };

  return (
    <Box className={classes.root}>
      <Grid container spacing={1} wrap="nowrap">
        {/* Trecho comentado de forma temporária para facilitar rollback caso a lista virtualizada cause problemas */}
        {/* <ListaAtendimentosIniciados
          resetSelectedFiles={handleResetInitialFiles}
        /> */}

        {/* Comente o componente abaixo caso a lista virtualizada cause problemas  */}
        <ListaAtendimentosIniciadosVirtualizada
          resetSelectedFiles={handleResetInitialFiles}
        />

        <Grid item xs component={Box} height="100%" position="relative">
          {/* Não tem nenhum atendimento iniciado ou aguardando na fila de espera */}
          {totalIniciados === 0 &&
            totalFila === 0 &&
            !initialLoadingMesssages &&
            !loadingAtendimentosIniciados && (
              <>
                {!currentUser?.disponivel && (
                  <Box mb={2}>
                    <AlertaIndisponibilidade />
                  </Box>
                )}

                <EmptyState
                  icon={
                    theme.palette.type === 'dark' ? (
                      <EmptyDarkImage />
                    ) : (
                      <EmptyLightImage />
                    )
                  }
                  title="Pausa para um café!"
                  description="Avisaremos quando um novo atendimento chegar"
                />
              </>
            )}

          {/* Não tem nenhum atendimento iniciado mas tem antendimentos na fila de espera */}
          {totalIniciados === 0 &&
            totalFila > 0 &&
            !initialLoadingMesssages && (
              <>
                <EmptyState
                  icon={
                    theme.palette.type === 'dark' ? (
                      <EmptyDarkImage />
                    ) : (
                      <EmptyLightImage />
                    )
                  }
                  title="Há pessoas aguardando por atendimento!"
                  description="Escolha alguém lá na fila de atendimentos em espera"
                />
              </>
            )}

          {/* Ainda tem atendimentos iniciados e nenhum atendimento selecionado */}
          {!atendimentoSelecionado &&
            totalIniciados > 0 &&
            !initialLoadingMesssages && (
              <EmptyState
                title="Que tal continuar de onde parou?!"
                description="Selecione um atendimento ao lado para dar continuidade"
              />
            )}

          {/* Carregando as primeiras mensagens do atendimento selecionado */}
          {atendimentoSelecionado && initialLoadingMesssages && (
            <EmptyState
              title="Um momento..."
              description="Estamos carregando as mensagens para você"
            />
          )}

          {atendimentoSelecionado && !initialLoadingMesssages && (
            <PasteOrDragAndDropFiles
              sessionClosed={
                atendimentoSelecionado?.sessaoStatus === StatusSessaoEnum.CLOSED
              }
              isInstagramOrMessengerChannel={
                atendimentoSelecionado?.canal?.tipoCanal ===
                  TipoCanalEnum.INSTAGRAM ||
                atendimentoSelecionado?.canal?.tipoCanal ===
                  TipoCanalEnum.MESSENGER
              }
              initialFiles={filesSelected}
              resetInitialFiles={handleResetInitialFiles}
              onSendFiles={handleSendFiles}
            >
              <TopContentAppBar
                onPesquisarMensagens={() => {
                  setPesquisarMensagens(prev => !prev);
                  dispatch(resetPreviewMensagens());
                }}
              />

              <NewMessagesContainer
                messages={mensagensAtendimento}
                next={onLoadNextMessages}
                previous={onLoadPreviousMessages}
                hasAfterCursor={
                  mensagemIdPesquisada
                    ? Boolean(atendimentoCursorAConsumir[0]?.afterCursor)
                    : Boolean(mensagensAtendimentoCursor.afterCursor)
                }
                hasBeforeCursor={
                  mensagemIdPesquisada
                    ? Boolean(atendimentoCursorAConsumir[0]?.beforeCursor)
                    : Boolean(mensagensAtendimentoCursor.beforeCursor)
                }
                loading={loadingMensagensAtendimento}
                endMessage={`Este é o início da sua conversa com ${atendimentoSelecionado.contato.nome}`}
                countMensagensNaoLidas={totalMsgsNaoLidas}
                onScrollToTop={handleConfirmarLeituraMensagens}
                onReenviarMensagem={handleReenviarMensagem}
                onApagarMensagem={handleApagarMensagem}
                onResponderMensagem={handleResponderMensagem}
                onEditarMensagem={onEditarMensagem}
                onResetLista={handleResetLista}
                onVerHistoricoMensagemEditada={
                  handleVerHistoricoMensagemEditada
                }
                searchQuery={pesquisarMensagens ? searchQuery : ''}
              />

              <ConditionalInputMessage
                atendimento={atendimentoSelecionado}
                replyMessage={replyMessage}
                onSendAudio={handleSendAudio}
                onSendCTA={handleSendCTA}
                onSendContato={handleSendContato}
                onSendFilesFromGaleria={handleSendFilesFromGaleria}
                onSelectFiles={handleSelectFiles}
                onSendTemplate={handleSendTemplate}
                onSendText={handleSendText}
                onSendInstagramTagMessage={handleSendInstagramTagMessage}
              />
            </PasteOrDragAndDropFiles>
          )}
        </Grid>

        {atendimentoSelecionado && (
          <Grid
            item
            xs
            lg={3}
            component={Box}
            height="100%"
            style={{ display: pesquisarMensagens ? 'block' : 'none' }}
          >
            <PesquisarMensagensContainer
              pesquisarMensagens={pesquisarMensagens}
              setPesquisarMensagens={setPesquisarMensagens}
              atendimento={atendimentoSelecionado}
              direction="left"
            />
          </Grid>
        )}
      </Grid>

      {loadingFinalizarAtendimento && <LoadingOverlay />}
    </Box>
  );
}
